import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../component/Navbar/NavbarCommon";
import Footer from "../component/Footer/FooterCommon";
import { api } from "../api";
import { API_URL, ASSETS_URL } from "../utils/config";
import { toast } from "react-toastify";
import moment from "moment";
import { ArrowLeft } from "lucide-react";
import ArrowButton from "../component/Common/ArrowButton";

export default function NewsEventDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const fetchDetails = async () => {
    try {
      const { data, error } = await api.get(`${API_URL.events}/${id}`);
      if (error) throw new Error(error.message);
      setItem((data as any)?.data);
    } catch (err) {
      toast.error(err.message || "Failed to fetch details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 text-lg">
        Loading...
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 text-lg">
        Item not found.
      </div>
    );
  }

  const isNews = item.type === "news";

  return (
    <div className="min-h-screen bg-gray-50 font-poppins">
      <Navbar />

      <div className="pt-28 pb-12 px-4 sm:px-6 lg:px-20 sm:mt-9 mb-24">
        <ArrowButton />

        {/* 📰 Conditional Layout */}
        {isNews ? (
          // 🗞️ Newspaper-style layout for News
          <div className="max-w-5xl mx-auto bg-[#f8f5f1] border border-gray-300 shadow-inner rounded-2xl overflow-hidden font-serif text-[#2b2b2b]">
            {item.imageUrl && (
              <img
                src={`${ASSETS_URL}${item.imageUrl}`}
                alt={item.title}
                className="w-full max-h-[450px] object-cover border-b border-gray-300"
              />
            )}

            <div className="p-6 sm:p-12">
              <h1 className="text-3xl sm:text-5xl font-extrabold text-center mb-6 tracking-tight uppercase leading-tight">
                {item.title}
              </h1>

              <div className="flex justify-center items-center gap-3 mb-6 text-sm text-gray-600 font-medium">
                <span>{moment(item.eventDate).format("MMMM D, YYYY")}</span>
                {item.location && (
                  <>
                    <span className="text-gray-400">|</span>
                    <span>{item.location}</span>
                  </>
                )}
              </div>

              <div className="border-t-4 border-double border-gray-700 my-6"></div>

              <div className="prose prose-lg max-w-none text-justify leading-relaxed text-gray-800">
  {item.description
    ?.split("\n")
    .filter((para) => para.trim() !== "")
    .map((para, idx) => (
      <p
        key={idx}
        className={`mb-4 ${
          idx === 0
            ? "first-letter:text-6xl first-letter:font-bold first-letter:float-left first-letter:mr-3 first-letter:leading-none first-letter:text-deepTeal"
            : ""
        }`}
      >
        {para}
      </p>
    ))}
</div>


              <div className="mt-12 text-sm text-center text-gray-500 italic">
                Published on {moment(item.eventDate).format("dddd, MMMM D, YYYY")}
              </div>

              {item.attachment && (
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4 mt-6 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-sm">Attachment</h3>
                      <p className="text-xs text-gray-500 truncate">Click to view document</p>
                    </div>
                    <a
                      href={`${ASSETS_URL}${item.attachment}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 inline-flex items-center gap-2 bg-blue-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>View</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          // 🎉 Modern card-style layout for Event
          <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100">
            {item.imageUrl && (
              <img
                src={`${ASSETS_URL}${item.imageUrl}`}
                alt={item.title}
                className="w-full h-72 sm:h-96 object-cover"
              />
            )}

            <div className="p-6 sm:p-10">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
                <span
                  className={`px-4 py-1.5 rounded-full text-sm font-medium ${
                    item.type === "event"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-green-100 text-green-700"
                  }`}
                >
                  {item.type === "event" ? "Event" : "News"}
                </span>

                <span className="text-gray-500 text-sm sm:text-base">
                  {moment(item.eventDate).format("MMMM Do, YYYY")}
                </span>
              </div>

              <h1 className="text-2xl sm:text-4xl font-bold text-deepTeal mb-4">
                {item.title}
              </h1>

              <p className="text-gray-700 leading-relaxed text-base sm:text-lg mb-8 whitespace-pre-line">
                {item.description}
              </p>

              {item.location && (
                <div className="bg-gray-50 border-l-4 border-green-500 p-4 rounded-lg mb-6">
                  <h3 className="font-semibold text-gray-800 mb-1">📍 Location</h3>
                  <p className="text-gray-600">{item.location}</p>
                </div>
              )}

              {item.eventDate && (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-1">🕒 Event Date</h3>
                  <p className="text-gray-600">
                    {moment(item.eventDate).format("dddd, MMMM Do YYYY")}
                  </p>
                </div>
              )}

              {item.attachment && (
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4 mt-6 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-sm">Attachment</h3>
                      <p className="text-xs text-gray-500 truncate">Click to view document</p>
                    </div>
                    <a
                      href={`${ASSETS_URL}${item.attachment}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 inline-flex items-center gap-2 bg-blue-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>View</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
